import React, { useRef } from 'react';
import { Box, Typography, Grid, MenuItem, FormControl } from '@mui/material';
import { Formik, Form, Field, FormikProps } from 'formik';
import * as Yup from 'yup';
import NewModal from '../../../Shared/NewModal';
import { TextInputFormField } from '../../../Shared/TextInputFormField';
import CustomerCareSelect from '../../../Shared/CustomerCareSelect';
import { ReactComponent as AssignUser } from '../../../../images/icons/assign_user.svg';
import { lightBlack, lightGrey, textFieldStyles } from '../../../../mainTheme';
import { CARETEAM_ROLES, CARETEAM_ROLE_VALUE } from '../../../../shared/roles';

const INVITE_ROLE_IDS: number[] = [
  CARETEAM_ROLE_VALUE.FAMILY_ADMIN,
  CARETEAM_ROLE_VALUE.FAMILY_CAREGIVER,
  CARETEAM_ROLE_VALUE.SOCIAL_CONTACT,
];

const INVITE_ROLE_OPTIONS = CARETEAM_ROLES.filter((role) =>
  INVITE_ROLE_IDS.includes(role.id),
);

export const buildValidationSchema = (ownerEmail?: string) =>
  Yup.object({
    first_name: Yup.string().trim().required('This field is required'),
    last_name: Yup.string().trim().required('This field is required'),
    email: Yup.string()
      .label('Email')
      .email()
      .required('This field is required')
      .test(
        'not-owner-email',
        "You can't add someone to their own care team.",
        (value) =>
          !ownerEmail ||
          !value ||
          value.trim().toLowerCase() !== ownerEmail.trim().toLowerCase(),
      ),
    role: Yup.string().required('This field is required'),
  });

interface InviteFormValues {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface InviteNewUserModalProps {
  onClose: () => void;
  onInvite: (data: InviteFormValues) => void;
  isLoading?: boolean;
  ownerEmail?: string;
}

const InviteNewUserModal: React.FC<InviteNewUserModalProps> = ({
  onClose,
  onInvite,
  isLoading = false,
  ownerEmail,
}) => {
  const formikRef = useRef<FormikProps<InviteFormValues>>(null);
  const validationSchema = React.useMemo(
    () => buildValidationSchema(ownerEmail),
    [ownerEmail],
  );

  return (
    <NewModal
      title="Invite new"
      maxWidth="xs"
      showCloseX
      onClose={onClose}
      actions={{
        secondaryAction: {
          text: 'Cancel',
          onClick: onClose,
        },
        primaryAction: {
          text: 'Invite',
          onClick: () => {
            formikRef.current && formikRef.current.submitForm();
          },
          disabled: false,
        },
      }}
      isLoading={isLoading}
    >
      <Box display="flex" flexDirection="column" gap={1.5} mb={2}>
        <AssignUser />
        <Typography>Invite new user</Typography>
      </Box>

      <Formik
        innerRef={formikRef}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          onInvite(values);
        }}
        initialValues={{
          first_name: '',
          last_name: '',
          email: '',
          role: '',
        }}
      >
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Field
                    label="First name"
                    name="first_name"
                    type="text"
                    placeholder="First name"
                    component={TextInputFormField}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    label="Last name"
                    name="last_name"
                    type="text"
                    placeholder="Last name"
                    component={TextInputFormField}
                    sx={textFieldStyles}
                  />
                </Grid>
              </Grid>
              <Field
                label="Email address"
                name="email"
                type="text"
                placeholder="Email address"
                component={TextInputFormField}
                sx={textFieldStyles}
              />
              <Field name="role">
                {({ field, form }: any) => (
                  <FormControl fullWidth>
                    <CustomerCareSelect
                      label="Role"
                      onChange={(e) =>
                        form.setFieldValue('role', e.target.value)
                      }
                      fullWidth
                      value={field.value}
                      renderSelectedValue={(value) => {
                        const option = INVITE_ROLE_OPTIONS.find(
                          (opt) => opt.id === Number(value),
                        );
                        return option?.label ?? '';
                      }}
                    >
                      {INVITE_ROLE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          sx={{ maxWidth: '396px', whiteSpace: 'normal' }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '20px',
                                letterSpacing: '0.2px',
                                color: lightBlack,
                              }}
                            >
                              {option.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: '12px',
                                lineHeight: '16px',
                                letterSpacing: 0,
                                color: lightGrey,
                                whiteSpace: 'normal',
                              }}
                            >
                              {option.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </CustomerCareSelect>
                  </FormControl>
                )}
              </Field>
            </Box>
          </Form>
        )}
      </Formik>
    </NewModal>
  );
};

export default InviteNewUserModal;
